import { useEffect, useState } from "react";
import api, { fileUrl } from "@/api/client";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { ArrowUpRight, PackageCheck, PackagePlus, PackageOpen, Clock, Activity, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const MetricCard = ({ label, value, delta, icon: Icon, testid, accent }) => (
  <div data-testid={testid} className={`border border-border p-6 bg-card hover-brut ${accent ? "shadow-brut-orange" : ""}`}>
    <div className="flex items-start justify-between">
      <div className="overline">{label}</div>
      <Icon size={16} className="text-primary" />
    </div>
    <div className="font-display text-5xl mt-3 leading-none">{value}</div>
    {delta != null && (
      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
        <ArrowUpRight size={12} className="text-primary" />
        <span className="mono">{delta}</span>
      </div>
    )}
  </div>
);

const StatusPill = ({ s }) => {
  const map = {
    received: "bg-accent/20 text-foreground border-accent",
    in_transit: "bg-primary/10 text-primary border-primary",
    delivered: "bg-emerald-500/15 text-emerald-700 border-emerald-500",
    forwarded: "bg-yellow-500/15 text-yellow-800 border-yellow-600",
    out_for_delivery: "bg-primary/15 text-primary border-primary",
    returned: "bg-destructive/10 text-destructive border-destructive",
    rto: "bg-primary text-primary-foreground border-primary font-bold",
  };
  const label = s === "rto" ? "RETURN TO OFFICE (RTO)" : s?.replace(/_/g, " ");
  return (
    <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${map[s] || "border-border"}`}>{label}</span>
  );
};

function BranchCompareCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canView = user && ["company_admin", "super_admin"].includes(user.role);
  useEffect(() => {
    if (!canView) return;
    api.get("/dashboard/branch-comparison", { params: { days: 30 } }).then((r) => setData(r.data)).catch(() => {});
  }, [canView]);
  if (!canView || !data || !data.rows?.length) return null;
  const COLORS = ["#BE1E1E", "#00E5FF", "#22c55e", "#eab308", "#a855f7", "#0A0A0A"];
  return (
    <section className="border border-border bg-card rounded-md p-5" data-testid="branch-compare-card">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={14} className="text-primary" />
        <div className="overline">Branch comparison · 30d</div>
      </div>
      <div className="font-display text-xl mb-4">Parcels per branch, stacked by courier</div>
      <div style={{ height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data.rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="branch" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {data.couriers.map((c, i) => (
              <Bar key={c} dataKey={c} stackId="a" fill={COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}


export default function DashboardPage() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="p-10 overline">Loading…</div>;

  return (
    <div className="p-8 space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Operations Console</div>
          <h1 className="text-5xl font-display mt-1">Dashboard.</h1>
        </div>
        <div className="flex gap-2">
          <Link data-testid="cta-inward" to="/app/inward" className="border border-foreground bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover-brut shadow-brut">+ Inward</Link>
          <Link data-testid="cta-outward" to="/app/outward" className="border border-foreground px-4 py-2 text-sm font-semibold hover-brut shadow-brut bg-card">+ Outward</Link>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="dashboard-metrics">
        {d.today > 0 && (
          <MetricCard label="Today" value={d.today} icon={Activity} testid="metric-today" accent />
        )}
        {d.inward > 0 && (
          <MetricCard label="Inward" value={d.inward} icon={PackagePlus} testid="metric-inward" />
        )}
        {d.outward > 0 && (
          <MetricCard label="Outward" value={d.outward} icon={PackageOpen} testid="metric-outward" />
        )}
        {d.pending > 0 && (
          <MetricCard label="Pending" value={d.pending} icon={Clock} testid="metric-pending" />
        )}
        {d.total > 0 && (
          <MetricCard label="Total Active" value={d.total} icon={PackageCheck} testid="metric-total" />
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border p-6 bg-card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">7-day parcel flow</div>
              <div className="font-display text-xl mt-1">Volume trend</div>
            </div>
            <span className="mono text-xs text-muted-foreground">count/day</span>
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={d.trend}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0 }} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ fill: "hsl(var(--primary))", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border p-6 bg-card">
          <div className="overline">Courier breakdown</div>
          <div className="font-display text-xl mt-1 mb-4">By courier</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={d.by_courier} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <YAxis type="category" dataKey="courier" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={80} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0 }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <BranchCompareCard />

      <section className="border border-border bg-card rounded-md" data-testid="dashboard-courier-perf">
        <div className="flex items-center justify-between p-5 border-b border-border flex-wrap gap-2">
          <div>
            <div className="overline">Courier performance · last 30d</div>
            <div className="font-display text-xl mt-1">Each courier's KPIs</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            SLA · inward 24h · outward 8h
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left overline">
                <th className="p-3">Courier</th>
                <th className="p-3 text-right">Parcels</th>
                <th className="p-3 text-right">Delivered</th>
                <th className="p-3 text-right">Weight</th>
                <th className="p-3 text-right">INR</th>
                <th className="p-3 text-right">Avg TAT</th>
                <th className="p-3 text-right">SLA %</th>
              </tr>
            </thead>
            <tbody>
              {(d.courier_performance || []).length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-xs">No courier data yet.</td></tr>
              )}
              {(d.courier_performance || []).map((c) => {
                const slaTone = c.sla_pct == null ? "text-muted-foreground"
                  : c.sla_pct >= 90 ? "text-emerald-700"
                  : c.sla_pct >= 70 ? "text-amber-700"
                  : "text-primary font-bold";
                return (
                  <tr key={c.courier} className="border-b border-border hover:bg-muted/40" data-testid={`dashboard-courier-${c.courier}`}>
                    <td className="p-3 font-semibold">{c.courier}</td>
                    <td className="p-3 mono text-right font-bold text-primary">{c.count}</td>
                    <td className="p-3 mono text-right text-emerald-700">{c.delivered}</td>
                    <td className="p-3 mono text-right">{c.weight_kg} kg</td>
                    <td className="p-3 mono text-right">₹{Number(c.amount_inr).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 mono text-right">{c.avg_tat_hours == null ? "—" : `${c.avg_tat_hours.toFixed(1)}h`}</td>
                    <td className={`p-3 mono text-right ${slaTone}`}>{c.sla_pct == null ? "—" : `${c.sla_pct.toFixed(1)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border border-border bg-card">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="overline">Live feed</div>
            <div className="font-display text-xl mt-1">Recent activity</div>
          </div>
          <Link data-testid="dashboard-view-all" to="/app/parcels" className="text-xs mono uppercase tracking-widest border border-foreground px-3 py-1 hover:bg-foreground hover:text-background">
            View all →
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left overline">
            <tr className="border-b border-border">
              <th className="p-4">Tracking</th>
              <th className="p-4">Dir.</th>
              <th className="p-4">Courier</th>
              <th className="p-4">Sender</th>
              <th className="p-4">Recipient</th>
              <th className="p-4">Status</th>
              <th className="p-4">By</th>
            </tr>
          </thead>
          <tbody>
            {d.recent.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No parcels yet — start with an Inward entry.</td></tr>
            )}
            {d.recent.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-muted/40">
                <td className="p-4 mono text-xs">{p.tracking_number}</td>
                <td className="p-4"><span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${p.direction === "inward" ? "border-accent text-accent-foreground bg-accent/10" : "border-primary text-primary"}`}>{p.direction}</span></td>
                <td className="p-4 mono">{p.courier}</td>
                <td className="p-4">{p.sender_name}</td>
                <td className="p-4">{p.recipient_name}</td>
                <td className="p-4"><StatusPill s={p.status} /></td>
                <td className="p-4 text-xs text-muted-foreground">{p.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
